import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import {
  CheckboxQuestion,
  Decorator,
  DropdownQuestion,
  HiddenQuestion,
  QuestionBase,
  QuestionGroup,
  TextareaQuestion,
  TextboxQuestion
} from 'core/components/forms';
import { UtilsService } from 'core/services/utils.service';
import { environment } from '../../../environments/environment';

const NAMEID_FORMATS = [
  'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent',
  'urn:oasis:names:tc:SAML:2.0:nameid-format:transient',
  'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
  'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified',
  'urn:oasis:names:tc:SAML:1.1:nameid-format:X509SubjectName',
  'urn:oasis:names:tc:SAML:1.1:nameid-format:WindowsDomainQualifiedName',
  'urn:oasis:names:tc:SAML:2.0:nameid-format:kerberos',
  'urn:oasis:names:tc:SAML:2.0:nameid-format:entity'
];
@Injectable()
export class ClientSsoQuestionsService {
  questions: QuestionBase<any>[];
  ssoQuestionGroup: QuestionGroup;
  configQuestionGroup: QuestionGroup;

  constructor(protected translateService: TranslateService) {}

  getQuestions(sid: string, ssoConfig?: any, isScormClient = false) {
    this.createSsoQuestionGroup(sid, ssoConfig, isScormClient);
    this.createConfigurationQuestionGroup(ssoConfig);

    this.questions = [this.ssoQuestionGroup, this.configQuestionGroup];
    return this.questions;
  }

  public createSsoQuestionGroup(sid: string, ssoConfig: any, isScormClient = false): QuestionGroup {
    this.ssoQuestionGroup = new QuestionGroup({
      title: 'clients.config.sso.label',
      isSection: true,
      childQuestions: [
        new HiddenQuestion({
          key: 'sid',
          value: sid
        }),
        // new HiddenQuestion({
        //   key: 'clientId',
        //   value: ssoConfig?.clientId,
        // }),
        // new HiddenQuestion({
        //   key: 'clientName',
        //   value: ssoConfig?.clientName,
        // }),
        new CheckboxQuestion({
          key: 'enabled',
          label: 'clients.config.sso.activate',
          value: ssoConfig ? ssoConfig.enabled : false,
          disabled: false,
          editable: !isScormClient
        }),
        new TextboxQuestion({
          key: 'clientPlatformURL',
          label: 'clients.config.sso.clientPlatformURL',
          value: ssoConfig ? ssoConfig.clientPlatformURL : null
        }),
        new DropdownQuestion({
          key: 'type',
          label: 'clients.config.sso.type',
          class: 'col-2-offset',
          placeholder: 'actions.select',
          options: [
            { key: 'sharedKey', value: this.translateService.instant('clients.config.sso.types.sharedKey') },
            { key: 'saml', value: this.translateService.instant('clients.config.sso.types.saml') }
          ],
          editable: environment.sids.campus === sid,
          value: ssoConfig ? ssoConfig.type : false,
          eventHandlers: {
            onChange: (type) => {
              this.questions[1] = this.createConfigurationQuestionGroup({ ...ssoConfig, type });
            }
          }
        })
      ]
    });
    return this.ssoQuestionGroup;
  }

  public createConfigurationQuestionGroup(ssoConfig?: any) {
    const type = ssoConfig?.type;
    this.configQuestionGroup = new QuestionGroup({
      title: 'nouns.config',
      key: 'config',
      isSection: true,
      hidden: !type,
      childQuestions: this.getConfigQuestions(type, ssoConfig.config)
    });
    return this.configQuestionGroup;
  }

  getConfigQuestions(type: string, config: any) {
    switch (type) {
      case 'saml':
        return this.getSamlQuestions(config);
      case 'sharedKey':
        return this.getSharedKeyQuestions(config);
      default:
        return [];
    }
  }

  public getSharedKeyQuestions(ssoConfig) {
    return [
      new TextboxQuestion({
        key: 'sharedKey',
        label: 'clients.config.sso.types.sharedKey',
        class: 'col-1',
        value: ssoConfig ? ssoConfig.sharedKey : null,
        validators: { required: true }
      }),
      new Decorator({
        label: 'clients.config.sso.generateSharedKey',
        buttonType: 'secondary',
        eventHandlers: {
          click: () => {
            const key = UtilsService.createGuid().split('-').join('');
            (this.configQuestionGroup.control as UntypedFormGroup).controls.sharedKey.setValue(key);
          }
        }
      })
    ];
  }

  public getSamlQuestions(ssoConfig) {
    return [
      new TextboxQuestion({
        key: 'loginUrl',
        label: 'clients.config.sso.loginUrl',
        class: 'col-1',
        value: ssoConfig ? ssoConfig.loginUrl : null,
        validators: { required: true }
      }),
      new TextboxQuestion({
        key: 'logoutUrl',
        label: 'clients.config.sso.logoutUrl',
        class: 'col-1',
        value: ssoConfig ? ssoConfig.logoutUrl : null,
        validators: { required: true }
      }),
      new DropdownQuestion({
        key: 'nameIdFormat',
        label: 'Name ID Format',
        value: ssoConfig?.nameIdFormat,
        options: [
          { key: '', value: 'Default (empty)' },
          ...NAMEID_FORMATS.map((format) => ({ key: format, value: format }))
        ]
      }),
      new TextboxQuestion({
        key: 'userAttributesMap',
        label: 'clients.config.sso.userAttributesMap',
        class: 'col-2',
        value: ssoConfig ? ssoConfig.userAttributesMap : null
      }),
      new TextboxQuestion({
        key: 'emailAttributeName',
        label: 'clients.config.sso.emailAttributeName',
        class: 'col-2',
        value: ssoConfig ? ssoConfig.emailAttributeName : null
      }),
      new TextareaQuestion({
        key: 'certificate',
        label: 'clients.config.sso.certificate',
        class: 'col-1',
        value: ssoConfig ? ssoConfig.certificate : null
      })
    ];
  }
}
